import React, {useCallback, useContext, useMemo, useState} from "react"
import PropTypes from "prop-types"
import {Grid, Stack, styled, Tab, Tabs} from "@mui/material"
import TabContent from "./ContainerTab/TabContent"
import Content from "../../components/Content"
import PrimaryButtonWidget from "../widgets/buttons/PrimaryButtonWidget"
import GhostButtonWidget from "../widgets/buttons/GhostbuttonWidget"
import {checkConditionsForHidden} from "../../misc/precondition-evaluation"
import {DataContext} from "../../../src/context/DataContextProvider"
import {SegmentButton} from "../widgets/SegmentButton/SegmentButton"

export const containerTabProps = [
    {
        key: "actionFirstBack",
        type: "STRING",
        label: "Aktion zurück (erste)",
        size: 3,
    },
    {
        key: "actionLastNext",
        type: "STRING",
        label: "Aktion weiter (letzte)",
        size: 3,
    },
    {
        key: "disableNextPrevButtons",
        type: "CHECKBOX",
        label: "Weiter/Zurück Buttons ausblenden",
        size: 6,
    },
    {
        key: "titleLastNext",
        type: "STRING",
        label: "Aktion weiter (letzte) titel",
        size: 6,
    },
    {
        key: "useSegmentButtons",
        type: "CHECKBOX",
        label: "Segment Buttons als Tabs",
        size: 6,
    },
]

const WEITER_TITLE = "weiter"
const SegmentButtonContainer = styled("div")(({theme}) => ({
    ...theme.SEGMENTBUTTON.container,
    [theme.breakpoints.down("md")]: {
        width: "100%",
        flexDirection: "column",
    },
}))

const ContainerTab = ({container, triggerAction}) => {
    const {
        children,
        titleLastNext,
        actionFirstBack,
        actionLastNext,
        disableNextPrevButtons = false,
        useSegmentButtons = false,
        action
    } = container
    const {getDataFromPointer} = useContext(DataContext)

    const [activeTab, setActiveTab] = useState(0)
    const onTabClick = (event, newValue) => {
        action && triggerAction(action, tabContainers[newValue])
        setActiveTab(newValue)
    }

    const isLastEl = useMemo(
        () => (children?.length ?? 0) === activeTab + 1,
        [activeTab, children?.length],
    )

    const hasNext = useMemo(() => {
        return actionLastNext || !isLastEl
    }, [isLastEl, actionLastNext])

    const hasBack = useMemo(() => {
        return actionFirstBack || activeTab !== 0
    }, [activeTab, actionFirstBack])

    const onClickBack = useCallback(() => {
        return actionFirstBack && activeTab === 0
            ? triggerAction(actionFirstBack)
            : setActiveTab((prevState) => prevState - 1)
    }, [triggerAction, actionFirstBack, activeTab])

    const onClickNext = useCallback(() => {
        return actionLastNext && isLastEl
            ? triggerAction(actionLastNext)
            : setActiveTab((prevState) => prevState + 1)
    }, [actionLastNext, triggerAction, isLastEl])

    const getNextTitle = useMemo(
        () => (isLastEl ? titleLastNext || WEITER_TITLE : WEITER_TITLE),
        [titleLastNext, isLastEl],
    )

    const checkForElementToHide = useCallback(
        (tabcontainer) => !checkConditionsForHidden(
            tabcontainer,
            getDataFromPointer,
        ),
        [getDataFromPointer],
    )

    const tabContainers = useMemo(
        () => children.filter(checkForElementToHide),
        [children, checkForElementToHide],
    )

    return (
        <Stack spacing={6} width="100%">
            {useSegmentButtons
                ? <SegmentButtonContainer>
                    {tabContainers?.map((tabcontainer, key) => (
                        <SegmentButton
                            key={key}
                            onClick={setActiveTab}
                            active={activeTab}
                            value={key}
                            label={tabcontainer.label}
                        />
                    ))}
                </SegmentButtonContainer>
                : <Tabs
                    value={activeTab}
                    onChange={onTabClick}>
                    {tabContainers
                        .map((tabcontainer, key) => (<Tab
                                label={tabcontainer.label}
                                key={`tab-${tabcontainer.id}`}
                                id={`tab-${key}`} />
                        ))}
                </Tabs>}
            {tabContainers
                .map((tabcontainer, key) => (
                    <TabContent
                        key={tabcontainer.id}
                        value={activeTab}
                        index={key}>
                        <Content child={tabcontainer} />
                    </TabContent>
                ))}
            {!disableNextPrevButtons && (
                <Grid
                    container
                    spacing={2}
                    justifyContent="flex-end">
                    {hasBack && (
                        <Grid>
                            <GhostButtonWidget
                                widget={{
                                    text: "zurück",
                                    onClick: onClickBack,
                                }}
                            />
                        </Grid>
                    )}
                    {hasNext && (
                        <Grid>
                            <PrimaryButtonWidget
                                widget={{
                                    text: getNextTitle,
                                    onClick: onClickNext,
                                }}
                            />
                        </Grid>
                    )}
                </Grid>
            )}
        </Stack>
    )
}

ContainerTab.propTypes = {
    container: PropTypes.object,
}

export default ContainerTab
